import { useState } from 'react';
import { Link } from 'react-router-dom';
import { ChevronRightIcon, LockIcon } from 'lucide-react';
import type { Rule, CreateRuleRequest, UpdateRuleRequest } from '@mockd/shared';
import type { Condition, ResponseStep, ActionDefinition } from '@mockd/shared/types/rule';
import { stripTemplatesForValidation } from '@mockd/shared/utils';
import { parseEditableConditions, buildConditions } from '../../utils/conditions';
import { ConditionEditor } from './ConditionEditor';
import { ResponseStepEditor } from './ResponseStepEditor';
import { ActionDefinitionEditor } from './ActionDefinitionEditor';
import { Alert } from '../common/Alert';
import type { RuleFormData } from './RulesPanel';
import { ROUTES } from '../../routes';

interface RuleFormProps {
  rule?: Rule;
  initialData?: Partial<RuleFormData>;
  tier: string;
  onSubmit: (data: CreateRuleRequest | UpdateRuleRequest) => Promise<void>;
  onCancel: () => void;
}

function getDefaultStep(): ResponseStep {
  return { statusCode: 200, body: '{}', headers: {}, count: 1 };
}

function getDefaultAction(): ActionDefinition {
  return { type: 'http_request', config: { url: '', method: 'POST' }, onError: 'continue' };
}

interface RuleFormInit {
  name: string;
  description: string;
  conditions: Condition[];
  matchMode: 'all' | 'any';
  conditionsEditable: boolean;
  steps: ResponseStep[];
  sequenceMode: 'loop' | 'hold_last';
  hasResponse: boolean;
  actions: ActionDefinition[];
  hasActions: boolean;
  capturesResponse: boolean;
  terminal: boolean;
}

function initFromRule(rule: Rule): RuleFormInit {
  const parsed = parseEditableConditions(rule.conditions);
  return {
    name: rule.name,
    description: rule.description || '',
    conditions: parsed.editable ? parsed.conditions : [],
    matchMode: parsed.editable ? parsed.mode : 'all',
    conditionsEditable: parsed.editable,
    steps: rule.response?.steps || [getDefaultStep()],
    sequenceMode: rule.response?.sequenceMode || 'loop',
    hasResponse: !!rule.response,
    actions: rule.actions || [getDefaultAction()],
    hasActions: !!rule.actions && rule.actions.length > 0,
    capturesResponse: rule.capturesResponse,
    terminal: rule.terminal,
  };
}

function initFromTemplate(data: Partial<RuleFormData>): RuleFormInit {
  return {
    name: data.name || '',
    description: data.description || '',
    conditions: data.conditions || [],
    matchMode: 'all',
    conditionsEditable: true,
    steps: data.response?.steps || [getDefaultStep()],
    sequenceMode: data.response?.sequenceMode || 'loop',
    hasResponse: !!data.response,
    actions: data.actions || [getDefaultAction()],
    hasActions: !!data.actions && data.actions.length > 0,
    capturesResponse: !!data.response,
    terminal: !!data.response,
  };
}

export function RuleForm({ rule, initialData, tier, onSubmit, onCancel }: RuleFormProps) {
  const init: RuleFormInit = rule
    ? initFromRule(rule)
    : initialData
      ? initFromTemplate(initialData)
      : { name: '', description: '', conditions: [], matchMode: 'all', conditionsEditable: true, steps: [getDefaultStep()], sequenceMode: 'loop' as const, hasResponse: true, actions: [getDefaultAction()], hasActions: false, capturesResponse: true, terminal: true };

  const [name, setName] = useState(init.name);
  const [description, setDescription] = useState(init.description);
  const [conditions, setConditions] = useState<Condition[]>(init.conditions);
  const [matchMode, setMatchMode] = useState<'all' | 'any'>(init.matchMode);
  // Nested groups / negation can't be edited here — preserve them untouched
  const conditionsEditable = init.conditionsEditable;

  // Response section
  const [hasResponse, setHasResponse] = useState(init.hasResponse);
  const [steps, setSteps] = useState<ResponseStep[]>(init.steps);
  const [sequenceMode, setSequenceMode] = useState<'loop' | 'hold_last'>(init.sequenceMode);

  // Control flow
  const [capturesResponse, setCapturesResponse] = useState(init.capturesResponse);
  const [terminal, setTerminal] = useState(init.terminal);

  const toggleHasResponse = (enabled: boolean) => {
    setHasResponse(enabled);
    // Both flags follow response presence, matching the API create defaults;
    // the toggles below let users override
    setCapturesResponse(enabled);
    setTerminal(enabled);
  };

  // Actions section — force disabled for free tier
  const isFree = tier === 'free';
  const [hasActions, setHasActions] = useState(isFree ? false : init.hasActions);
  const [actions, setActions] = useState<ActionDefinition[]>(init.actions);

  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  // Collapsible sections
  const [responseOpen, setResponseOpen] = useState(init.hasResponse);
  const [actionsOpen, setActionsOpen] = useState(init.hasActions);

  const validate = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!name.trim()) {
      newErrors.name = 'Name is required';
    }

    if (!hasResponse && !hasActions) {
      newErrors.general = 'Rule must have either a response or actions (or both)';
    }

    if (hasResponse) {
      if (steps.length === 0) {
        newErrors.steps = 'At least one response step is required';
      }
      for (let i = 0; i < steps.length; i++) {
        const step = steps[i];
        if (step.statusCode < 100 || step.statusCode > 599) {
          newErrors[`step_${i}_status`] = `Step ${i + 1}: Invalid status code`;
        }
        if (!step.body.trim()) {
          newErrors[`step_${i}_body`] = `Step ${i + 1}: Body is required`;
        } else {
          try {
            JSON.parse(stripTemplatesForValidation(step.body));
          } catch {
            newErrors[`step_${i}_body`] = `Step ${i + 1}: Invalid JSON body`;
          }
        }
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!validate()) return;

    setSubmitting(true);
    try {
      const builtConditions = buildConditions(matchMode, conditions);
      const data: CreateRuleRequest | UpdateRuleRequest = {
        name: name.trim(),
        description: description.trim() || undefined,
        // Non-editable (nested/negated) conditions are left unchanged on update
        ...(conditionsEditable && {
          conditions: builtConditions ?? (rule ? null : undefined),
        }),
        response: hasResponse
          ? { steps, sequenceMode }
          : rule ? null : undefined,
        actions: hasActions && actions.length > 0
          ? actions
          : rule ? null : undefined,
        capturesResponse: hasResponse ? capturesResponse : false,
        terminal,
      };
      await onSubmit(data);
    } catch (err) {
      setErrors({ submit: err instanceof Error ? err.message : 'Failed to save rule' });
    } finally {
      setSubmitting(false);
    }
  };

  // Step management
  const updateStep = (index: number, updated: ResponseStep) => {
    const next = [...steps];
    next[index] = updated;
    setSteps(next);
  };

  const removeStep = (index: number) => {
    setSteps(steps.filter((_, i) => i !== index));
  };

  const moveStep = (from: number, to: number) => {
    const next = [...steps];
    const [moved] = next.splice(from, 1);
    next.splice(to, 0, moved);
    setSteps(next);
  };

  // Action management
  const updateAction = (index: number, updated: ActionDefinition) => {
    const next = [...actions];
    next[index] = updated;
    setActions(next);
  };

  const removeAction = (index: number) => {
    setActions(actions.filter((_, i) => i !== index));
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4">
      {/* Name & Description */}
      <div className="grid grid-cols-2 gap-4">
        <div className="fieldset min-w-0">
          <label className="fieldset-legend">Name</label>
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Return user by ID"
            className={`input w-full ${errors.name ? 'input-error' : ''}`}
          />
          {errors.name && <p className="text-error text-sm mt-1">{errors.name}</p>}
        </div>

        <div className="fieldset min-w-0">
          <label className="fieldset-legend">Description</label>
          <input
            type="text"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            placeholder="Optional description"
            className="input w-full"
          />
        </div>
      </div>

      {/* Conditions */}
      <div className="border-t border-base-200 pt-4">
        <h4 className="font-medium text-base-content mb-3">Conditions</h4>
        {conditionsEditable ? (
          <ConditionEditor
            conditions={conditions}
            onChange={setConditions}
            matchMode={matchMode}
            onMatchModeChange={setMatchMode}
          />
        ) : (
          <p className="text-sm text-base-content/70 italic">
            This rule uses nested condition groups, which can&apos;t be edited here yet.
            They will be kept as-is when you save. Use the API to change them.
          </p>
        )}
      </div>

      {/* Response Section */}
      <div className="border-t border-base-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => {
              setResponseOpen(!responseOpen);
              if (!hasResponse && !responseOpen) {
                toggleHasResponse(true);
              }
            }}
            className="flex items-center gap-2"
          >
            <ChevronRightIcon className={`size-4 transition-transform ${responseOpen ? 'rotate-90' : ''}`} />
            <h4 className="font-medium text-base-content">Response</h4>
            {!hasResponse && (
              <span className="text-xs text-base-content/70">(disabled)</span>
            )}
          </button>

          <label className="flex items-center gap-2 cursor-pointer">
            <span className="text-xs text-base-content/70">Enabled</span>
            <input
              type="checkbox"
              checked={hasResponse}
              onChange={(e) => {
                toggleHasResponse(e.target.checked);
                if (e.target.checked) setResponseOpen(true);
              }}
              className="checkbox checkbox-primary checkbox-sm"
            />
          </label>
        </div>

        {responseOpen && hasResponse && (
          <div className="space-y-3 ml-6">
            {steps.length > 1 && (
              <div className="flex items-center gap-3 mb-2">
                <span className="text-sm text-base-content/70">After last step:</span>
                <select
                  value={sequenceMode}
                  onChange={(e) => setSequenceMode(e.target.value as 'loop' | 'hold_last')}
                  className="select select-sm"
                >
                  <option value="loop">Loop back to first</option>
                  <option value="hold_last">Hold on last</option>
                </select>
              </div>
            )}

            {steps.map((step, index) => (
              <ResponseStepEditor
                key={index}
                step={step}
                index={index}
                total={steps.length}
                onChange={(s) => updateStep(index, s)}
                onRemove={() => removeStep(index)}
                onMoveUp={() => moveStep(index, index - 1)}
                onMoveDown={() => moveStep(index, index + 1)}
              />
            ))}

            <button
              type="button"
              onClick={() => setSteps([...steps, getDefaultStep()])}
              className="btn btn-ghost btn-sm"
            >
              + Add Step
            </button>

            {/* Step validation errors */}
            {Object.entries(errors)
              .filter(([k]) => k.startsWith('step_'))
              .map(([k, v]) => (
                <p key={k} className="text-error text-sm">{v}</p>
              ))}
          </div>
        )}
      </div>

      {/* Actions Section */}
      <div className="border-t border-base-200 pt-4">
        <div className="flex items-center justify-between mb-3">
          <button
            type="button"
            onClick={() => {
              if (isFree) return;
              setActionsOpen(!actionsOpen);
              if (!hasActions && !actionsOpen) {
                setHasActions(true);
              }
            }}
            className="flex items-center gap-2"
          >
            <ChevronRightIcon className={`size-4 transition-transform ${actionsOpen && !isFree ? 'rotate-90' : ''}`} />
            <h4 className="font-medium text-base-content">Actions</h4>
            {isFree ? (
              <LockIcon className="size-4 text-base-content/70" />
            ) : !hasActions ? (
              <span className="text-xs text-base-content/70">(disabled)</span>
            ) : null}
          </button>

          {!isFree && (
            <label className="flex items-center gap-2 cursor-pointer">
              <span className="text-xs text-base-content/70">Enabled</span>
              <input
                type="checkbox"
                checked={hasActions}
                onChange={(e) => {
                  setHasActions(e.target.checked);
                  if (e.target.checked) setActionsOpen(true);
                }}
                className="checkbox checkbox-primary checkbox-sm"
              />
            </label>
          )}
        </div>

        {isFree && (
          <div className="ml-6 p-4 bg-base-200/50 rounded-box">
            <p className="text-sm text-base-content/70">
              Rule actions (HTTP callouts, set variable, store variable) require a Pro plan.
            </p>
            {/* A new tab: navigating away would unmount this form and lose the rule. */}
            <Link to={ROUTES.pricing()} target="_blank" rel="noopener" className="btn btn-sm btn-primary mt-2">
              Upgrade to Pro <span className="sr-only">(opens in a new tab)</span>
            </Link>
          </div>
        )}

        {!isFree && actionsOpen && hasActions && (
          <div className="space-y-3 ml-6">
            {actions.map((action, index) => (
              <ActionDefinitionEditor
                key={index}
                action={action}
                index={index}
                onChange={(a) => updateAction(index, a)}
                onRemove={() => removeAction(index)}
              />
            ))}

            <button
              type="button"
              onClick={() => setActions([...actions, getDefaultAction()])}
              className="btn btn-ghost btn-sm"
            >
              + Add Action
            </button>
          </div>
        )}
      </div>

      {/* Evaluation / control flow */}
      <div className="border-t border-base-200 pt-4">
        <h4 className="font-medium text-base-content mb-3">Evaluation</h4>
        <div className="space-y-3 ml-6">
          <label className={`flex items-start gap-3 ${hasResponse ? 'cursor-pointer' : 'opacity-50'}`}>
            <input
              type="checkbox"
              checked={hasResponse && capturesResponse}
              disabled={!hasResponse}
              onChange={(e) => setCapturesResponse(e.target.checked)}
              className="checkbox checkbox-primary checkbox-sm mt-0.5"
            />
            <span>
              <span className="text-sm text-base-content block">Use this rule&apos;s response</span>
              <span className="text-xs text-base-content/60 block">
                When off, the response is kept as a draft and a later rule (or the endpoint default) answers the request.
              </span>
            </span>
          </label>

          <label className="flex items-start gap-3 cursor-pointer">
            <input
              type="checkbox"
              checked={terminal}
              onChange={(e) => setTerminal(e.target.checked)}
              className="checkbox checkbox-primary checkbox-sm mt-0.5"
            />
            <span>
              <span className="text-sm text-base-content block">Stop evaluating rules after match</span>
              <span className="text-xs text-base-content/60 block">
                When off, later rules still run their actions after this rule matches. The first captured response wins.
              </span>
            </span>
          </label>
        </div>
      </div>

      {/* Errors */}
      {errors.general && (
        <Alert variant="error">{errors.general}</Alert>
      )}
      {errors.submit && (
        <Alert variant="error">{errors.submit}</Alert>
      )}

      {/* Buttons */}
      <div className="flex justify-end gap-3 pt-4 border-t border-base-200">
        <button
          type="button"
          onClick={onCancel}
          className="btn btn-ghost"
        >
          Cancel
        </button>
        <button
          type="submit"
          disabled={submitting}
          className="btn btn-primary"
        >
          {submitting ? 'Saving...' : rule ? 'Update Rule' : 'Create Rule'}
        </button>
      </div>
    </form>
  );
}
